/**
 * Presentation Layer - GoogleSheetsExportResult Component
 * Google Sheetsエクスポート結果表示コンポーネント
 */

import React from 'react';
import { Alert, AlertTitle, Typography, Link, Box, Chip, Stack } from '@mui/material';
import { OpenInNew as OpenIcon, TableChart as SheetIcon } from '@mui/icons-material';
import type { OutsideTimeExportResult } from '../../../../domain/medicalReservation/OutsideTimeExportResult';

/**
 * Props定義
 */
export interface GoogleSheetsExportResultProps {
  /** エクスポート結果 */
  result: OutsideTimeExportResult | null;
}

/**
 * Google Sheetsエクスポート結果表示コンポーネント
 */
export const GoogleSheetsExportResult: React.FC<GoogleSheetsExportResultProps> = ({
  result,
}) => {
  if (!result) {
    return null;
  }

  return (
    <Alert
      severity={result.success ? 'success' : 'error'}
      sx={{ my: 2 }}
      icon={<SheetIcon />}
    >
      <AlertTitle>
        {result.success
          ? 'Google Sheetsへのエクスポート完了'
          : 'Google Sheetsエクスポートエラー'}
      </AlertTitle>

      {result.success ? (
        <Box>
          {/* 成功時の詳細 */}
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
            {result.sheetName && (
              <Chip
                label={`シート: ${result.sheetName}`}
                size="small"
                variant="outlined"
              />
            )}
            {result.rowsWritten !== undefined && (
              <Chip
                label={`${result.rowsWritten} 行`}
                size="small"
                color="success"
              />
            )}
            {result.timeRange && (
              <Chip
                label={result.timeRange}
                size="small"
                variant="outlined"
              />
            )}
          </Stack>

          {/* スプレッドシートリンク */}
          {result.spreadsheetUrl && (
            <Link
              href={result.spreadsheetUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              スプレッドシートを開く
              <OpenIcon fontSize="small" />
            </Link>
          )}
        </Box>
      ) : (
        <Typography variant="body2" color="error">
          {result.error || 'エクスポートに失敗しました'}
        </Typography>
      )}
    </Alert>
  );
};
